import axios from 'helpers/axios';
import config from 'helpers/config';
import Customer from 'models/CustomerModel';
import SearchResults from 'models/SearchResultsModel';
import React, { useState } from 'react';
import { FaEnvelope, FaPhoneSquare, FaTimesCircle } from 'react-icons/fa';
import { connect } from 'react-redux';

interface Props {
    customerSelected: (customerId?: Customer) => void;
    displayDetailsWhenSelected?: boolean;

    alreadySelectedCustomer?: Customer;
}

const CustomerSearch: React.FC<Props> = ({
    displayDetailsWhenSelected = false,
    customerSelected,
    alreadySelectedCustomer,
}: Props) => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [searchPhrase, setSearchPhrase] = useState('');
    const [isSearchLocked, setIsSearchLocked] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

    React.useEffect(() => {
        if (alreadySelectedCustomer) {
            setSelectedCustomer(alreadySelectedCustomer);
        }
    });

    const fetchCustomers = (searchPhrase: string) => {
        const itemsPerPage = 10,
            page = 1;

        setSearchPhrase(searchPhrase);

        axios
            .get<SearchResults<Customer>>(`${config.apiUrl}/customer`, { params: { page, itemsPerPage, searchPhrase } })
            .then((res) => {
                setCustomers(res.data.rows);
            })
            .catch((_) => {
                setCustomers([]);
            });
    };

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomer(customer);
        customerSelected(customer);
        setCustomers([]);
        setIsSearchActive(false);
        setIsSearchLocked(false);
    };

    return (
        <div className="relative">
            {!!selectedCustomer ? (
                <div className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between p-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                {selectedCustomer.firstName?.charAt(0)}
                                {selectedCustomer.lastName?.charAt(0)}
                            </div>
                            <div>
                                <div className="font-semibold text-gray-900 text-base">
                                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                                </div>
                                <div className="text-sm text-gray-600 font-medium">Selected Customer</div>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setSelectedCustomer(undefined);
                                customerSelected(undefined);
                            }}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-200"
                            title="Remove selection"
                        >
                            <FaTimesCircle className="w-5 h-5" />
                        </button>
                    </div>
                    {displayDetailsWhenSelected && (
                        <div className="px-4 pb-4 border-t border-blue-100 pt-3 bg-white bg-opacity-50">
                            <div className="space-y-2">
                                {selectedCustomer.phoneNumbers?.map((number) => (
                                    <div key={number.id} className="flex items-center text-sm text-gray-700">
                                        <div className="w-8 flex justify-center">
                                            <FaPhoneSquare className="text-blue-500 w-4 h-4" />
                                        </div>
                                        <span className="font-medium">{number.phoneNumber}</span>
                                    </div>
                                ))}
                                {selectedCustomer.primaryEmail && (
                                    <div className="flex items-center text-sm text-gray-700">
                                        <div className="w-8 flex justify-center">
                                            <FaEnvelope className="text-blue-500 w-4 h-4" />
                                        </div>
                                        <a
                                            href={`mailto:${selectedCustomer.primaryEmail}`}
                                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150"
                                        >
                                            {selectedCustomer.primaryEmail}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <input
                    type="text"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 bg-white focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 outline-none transition-all duration-150 placeholder-gray-400 text-sm"
                    onChange={(e) => fetchCustomers(e.target.value)}
                    onFocus={() => setIsSearchActive(true)}
                    onBlur={() => {
                        setTimeout(() => {
                            if (isSearchLocked) {
                                return;
                            }
                            setIsSearchActive(false);
                        }, 100);
                    }}
                    placeholder="Search by name, phone number or email"
                />
            )}
            {!selectedCustomer && isSearchActive && searchPhrase && (
                <div
                    className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-64 overflow-y-auto"
                    onMouseOver={() => setIsSearchLocked(true)}
                    onMouseLeave={() => setIsSearchLocked(false)}
                >
                    {customers &&
                        customers?.map((customer, index) => (
                            <div
                                key={index}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                                onClickCapture={() => handleCustomerSelect(customer)}
                            >
                                <div className="font-semibold text-gray-900 mb-1">
                                    {customer.firstName} {customer.lastName}
                                </div>
                                <div className="space-y-1">
                                    {customer.phoneNumbers.map((number) => (
                                        <div key={number.id} className="text-sm text-gray-600 flex items-center">
                                            <FaPhoneSquare className="mr-2 text-gray-400" />
                                            {number.phoneNumber}
                                        </div>
                                    ))}
                                    {customer.primaryEmail && (
                                        <div className="text-sm text-gray-600 flex items-center">
                                            <FaEnvelope className="mr-2 text-gray-400" />
                                            {customer.primaryEmail}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    {/* also check that input is not empty */}
                    {customers?.length < 1 && searchPhrase.length > 0 && (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-500 bg-white rounded-lg">
                            <div className="text-gray-400 mb-2">
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                </svg>
                            </div>
                            <div className="font-medium text-gray-600">No customers found</div>
                            <div className="text-sm text-gray-400 mt-1">Try adjusting your search terms</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(CustomerSearch);
